import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

// Expanded FAQ — the on-page version on Landing has 12 core questions.
// This page adds 8 more that specifically target keywords LLM crawlers see
// when someone asks "what is a packaging audit", "how much does it cost",
// "AI vs human packaging critique", etc.
const FAQS = [
  {
    section: "About the platform",
    items: [
      {
        q: "What is a packaging audit?",
        a: "A packaging audit is a structured evaluation of a physical pack's design against the objectives it's meant to achieve — building brand equity, being noticed on shelf, being understood in three seconds, being chosen over competitors. Traditional audits are done by senior designers or brand strategists. Verdict makes the same evaluation available on-demand, powered by AI critique that has been anchored on Therefore Design's 7-dimension framework.",
      },
      {
        q: "What is Verdict?",
        a: "Verdict is a Human-in-the-Loop (HITL) AI-powered Packaging Intelligence Platform built by Therefore Design. It critiques packaging artwork across seven strategic dimensions tuned for the Indian retail market — brand alignment, e-commerce shelf, aisle resiliency, traditional (kirana) retail, distinctiveness, hierarchy, and audience aspiration fit — and produces a written verdict plus a shareable branded PDF report.",
      },
      {
        q: "Who is Verdict built for?",
        a: "Verdict is built for design studios, brand teams, and packaging-led founders who need a candid, on-demand second opinion on a pack before it ships. It's purpose-tuned for the Indian market — kirana, modern trade, e-commerce thumbnails, regional languages.",
      },
      {
        q: "Is Verdict a replacement for a design brief or a design agency?",
        a: "No. Verdict is a critique layer, not a creative team. It stress-tests decisions your designer or agency has already made, and gives you an evidence-based second opinion before you print. Most teams use it in-parallel with their design partner, not instead of.",
      },
    ],
  },
  {
    section: "The methodology",
    items: [
      {
        q: "How is the seven-dimension score calculated?",
        a: "Each pack image is critiqued one dimension at a time by a frontier multimodal LLM (Claude, GPT, or Gemini — you pick per audit). Every dimension returns its own score out of 10, strengths, weaknesses, and improvements. A final synthesis pass produces the overall verdict and the priority recommendations. Verdict scores conservatively: a 6 or higher means the pack is ship-ready with minimal fixes; anything needing a redesign is capped at 5.",
      },
      {
        q: "How accurate is the AI scoring?",
        a: "Verdict is built on packaging strategy principles Therefore has used in real-world brand and design work for 15+ years. It's designed to reflect how a trained strategist would critique a pack — not just how it looks, but how well it works. Because it's a Human-in-the-Loop platform, every score is meant to be reviewed and adjusted by a designer or brand lead before it drives a decision.",
      },
      {
        q: "How is AI-powered packaging critique different from a human design review?",
        a: "AI is fast, consistent, and available on-demand at midnight. A human review is context-rich, culturally intuitive, and can push back on a client. Verdict combines the strengths of both: the AI runs the seven-dimension framework in minutes on any pack, and the human designer treats the output as input — refining, disagreeing, or building on it.",
      },
      {
        q: "Is Verdict tuned for the Indian market specifically?",
        a: "Yes. Every dimension is calibrated for Indian retail context: kirana shelves under fluorescent light, modern-trade gondolas, e-commerce thumbnails on mobile, price-point pack sizes, FSSAI mandatory copy, and the fact that English-only packs are broadly acceptable across urban, semi-urban, and most GT markets.",
      },
    ],
  },
  {
    section: "Pricing and access",
    items: [
      {
        q: "How much does a packaging audit cost?",
        a: "Pay-per-audit in INR via Razorpay. New external users get one audit free with no card required. Bundle pricing (10, 25, 50 audits) reduces the per-audit price for teams running multiple assessments a month. See the current tiers on the Pricing page.",
      },
      {
        q: "How do I get access?",
        a: "Anyone with a @thereforedesign.co.in Google account signs in automatically. External designers (freelancers, partner agencies, brand teams) sign in with Google, get a free trial credit, and can top up their wallet whenever they want to run more audits.",
      },
      {
        q: "Can I get an invoice / tax receipt?",
        a: "Yes — every Razorpay purchase generates a GST-compliant invoice. Write to info@thereforedesign.co.in if you need a custom invoice with your company GSTIN.",
      },
    ],
  },
  {
    section: "Using it in practice",
    items: [
      {
        q: "Can I use it to compare multiple packaging options?",
        a: "Yes — you can run audits on different pack variants and use the Side-by-side diff view to compare performance across every dimension. It's how most teams pick between three or four short-listed design directions.",
      },
      {
        q: "Can I compare a refresh against the previous pack?",
        a: "Yes. Mark the new design as a Refresh / Sub-premise and upload the existing on-shelf pack at the project stage — every audit then scores improvement against it and adds a side-by-side delta section to the report.",
      },
      {
        q: "Is it useful for packaging that's already in market?",
        a: "Absolutely. Many teams use Verdict to benchmark existing packs, build a case for a redesign to their marketing head, or identify specific weak points to fix without committing to a full overhaul.",
      },
      {
        q: "Do I need design expertise to interpret the results?",
        a: "No. The scores and commentary are written for brand managers and marketers, not just designers. The PDF report is polished enough to walk into a boardroom with.",
      },
      {
        q: "Can I track how my packaging has improved over time?",
        a: "Yes. Verdict saves your audit history so you can revisit previous results, track progress across iterations, and see how each round of changes moved the needle.",
      },
      {
        q: "Can I collaborate with my team on an audit?",
        a: "Yes. Designers and brand leads can invite collaborators to a project. Team members can review the audit results and leave comments directly, keeping feedback in one place rather than scattered across emails and calls.",
      },
      {
        q: "How do I share results with stakeholders who aren't on the platform?",
        a: "You can email the audit results to anyone as a polished, branded PDF — no login required on their end. It's presentation-ready, so it works just as well in a client meeting as it does in an internal review.",
      },
    ],
  },
  {
    section: "Data & privacy",
    items: [
      {
        q: "What if my design is confidential?",
        a: "Uploads are stored only on Therefore Design's own backend. Per Anthropic, Google AI, and OpenAI API terms, API inputs are NOT used to train their general models. Inside the studio dashboard you can delete any project, asset, or assessment at any time.",
      },
      {
        q: "Where is my data hosted?",
        a: "Backend and database are hosted on infrastructure managed by Emergent, with encryption at rest. No third-party analytics receives your audit content or account data — we only track anonymous, aggregate page views via Google Analytics (and only after you accept the cookie banner).",
      },
      {
        q: "Can I delete my data?",
        a: "Yes. Every project has a delete option, and if you need your entire account and all associated data wiped, write to info@thereforedesign.co.in and we'll process it within 7 working days.",
      },
    ],
  },
];

export default function FAQ() {
  // Flat list built once for both rendering and JSON-LD (schema.org FAQPage
  // needs mainEntity as an array of Question nodes — no section grouping).
  const flat = FAQS.flatMap((s) => s.items);
  const [openKey, setOpenKey] = useState(flat[0]?.q || null);

  return (
    <div className="v-container" style={{ paddingTop: 96, paddingBottom: 140, maxWidth: 1080 }} data-testid="faq-page">
      <Seo
        path="/faq"
        title="FAQ — packaging audit basics, pricing, methodology"
        description="Every honest question about the Verdict packaging audit platform — what a packaging audit is, how the AI scoring works, how much it costs, how it handles refresh vs new packaging, and how your design data is protected."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: flat.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://verdict.thereforedesign.co.in/" },
              { "@type": "ListItem", position: 2, name: "FAQ", item: "https://verdict.thereforedesign.co.in/faq" },
            ],
          },
        ]}
      />

      <div className="overline" style={{ color: "var(--v-accent)" }}>FAQ</div>
      <h1 className="display" style={{ fontSize: "clamp(40px, 5.5vw, 72px)", marginTop: 12, lineHeight: 1.02, letterSpacing: "-.02em" }}>
        The honest questions.
      </h1>
      <p style={{ marginTop: 20, color: "var(--v-text-2)", maxWidth: 760, fontSize: 17, lineHeight: 1.65 }}>
        The ones designers ask before they hit upload — and the ones brand custodians ask after.
        If your question isn&apos;t answered here, write to <a href="mailto:info@thereforedesign.co.in" style={{ color: "var(--v-accent)" }}>info@thereforedesign.co.in</a>.
      </p>

      {FAQS.map((section, sIdx) => (
        <div key={section.section} style={{ marginTop: sIdx === 0 ? 64 : 56 }} data-testid={`faq-section-${sIdx}`}>
          <div className="overline">{section.section}</div>
          <div style={{ marginTop: 16 }}>
            {section.items.map((f, i) => {
              const open = openKey === f.q;
              return (
                <div
                  key={f.q}
                  data-testid={`faq-item-${sIdx}-${i}`}
                  style={{ borderTop: "1px solid var(--v-border)" }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenKey(open ? null : f.q)}
                    aria-expanded={open}
                    style={{
                      width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: 16, padding: "22px 0",
                      background: "transparent", border: "none", textAlign: "left",
                      color: "inherit", cursor: "pointer",
                    }}
                  >
                    <span style={{ fontSize: 17, fontWeight: 500, letterSpacing: "-.005em" }}>{f.q}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: 22, fontWeight: 300, color: "var(--v-accent)",
                        transform: open ? "rotate(45deg)" : "rotate(0deg)",
                        transition: "transform .25s ease", lineHeight: 1,
                      }}
                    >+</span>
                  </button>
                  <div
                    style={{
                      maxHeight: open ? 640 : 0,
                      overflow: "hidden",
                      transition: "max-height .35s ease",
                    }}
                  >
                    <p style={{ paddingBottom: 22, color: "var(--v-text-2)", fontSize: 15, lineHeight: 1.7, maxWidth: 760 }}>
                      {f.a}
                    </p>
                  </div>
                </div>
              );
            })}
            {/* Add the closing border to the last section */}
            {sIdx === FAQS.length - 1 && <div style={{ borderTop: "1px solid var(--v-border)" }} />}
          </div>
        </div>
      ))}

      <div className="v-surface" style={{ marginTop: 96, padding: "clamp(32px, 5vw, 56px)", textAlign: "center" }}>
        <h2 className="display" style={{ fontSize: "clamp(28px, 4vw, 44px)", lineHeight: 1.05, letterSpacing: "-.015em" }}>
          Ready to run one?
        </h2>
        <p style={{ marginTop: 16, color: "var(--v-text-2)", fontSize: 15, lineHeight: 1.6 }}>
          First audit is free. No card required.
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/" className="v-btn" data-testid="faq-cta-run">Run your first Verdict →</Link>
          <Link to="/framework" className="v-btn v-btn-outline" data-testid="faq-cta-framework">Read the framework →</Link>
        </div>
      </div>
    </div>
  );
}
